import { FC, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { VoiceRecorder } from './VoiceRecorder';
import { api, Post } from '../services/api';
import { PulseButton } from './ui/PulseButton';
import { Icon } from './icons/Icon';
import { COPY } from '../lib/copyDictionary';
import { useGamenics, GamenicsEvent } from '../hooks/useGamenics';
import { RoleEntryButtons } from './RoleEntryButtons';

interface ExistingChild {
    id: string;
    contentType: string;
    fileUrl: string;
    description?: string;
    durationSec?: number;
    authorName: string;
    textContent?: string;
}

interface SubmissionModalProps {
    postId: string;
    role?: 'script' | 'illustration' | 'voice';
    existingChildren?: ExistingChild[];
    onClose: () => void;
    onSuccess: (createdPostId?: string) => void; // 作成された post.id を渡す（既存の () => void 呼び出し元は無視・互換維持）
    // ── PIPE-01: 街区成果物からのプリフィル起動（省略時は従来の通常投稿・互換維持） ──
    initialText?: string;                 // サロン: draft_text をscript本文にプリフィル
    initialTitle?: string;                // 街区成果物のタイトル
    initialDescription?: string;          // 説明のプリフィル
    initialImageBlob?: Blob;              // トキワ荘: 保存済みPNGをプリフィル
    initialAudioBlob?: Blob;              // ステージ: 録音をプリフィル
    originRef?: { space: string; item_id: string; version?: number }; // 作品化の逆参照（これが有ると PIPE-01 モード）
}

// ─── Existing Content Preview (shown during creation) ─────
const ExistingPreview: FC<{
    children: ExistingChild[];
    currentRole: string;
}> = ({ children, currentRole }) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [playing, setPlaying] = useState(false);

    const textChild = children.find(c => c.contentType === 'text');
    const imageChild = children.find(c => c.contentType === 'image');
    const audioChild = children.find(c => c.contentType === 'audio');

    // Don't show preview of the same type being created
    const hasRelevantContent = (currentRole === 'script' && (imageChild || audioChild))
        || (currentRole === 'illustration' && (textChild || audioChild))
        || (currentRole === 'voice' && (textChild || imageChild));

    if (!hasRelevantContent) return null;

    const toggleAudio = () => {
        if (!audioRef.current) return;
        if (playing) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setPlaying(!playing);
    };

    return (
        <div className="bg-cream border border-ink/15 rounded-xl overflow-hidden mb-4">
            <div className="px-3 py-2 border-b border-ink/10">
                <span className="text-[10px] font-mono text-ink-3 uppercase tracking-wider">既存の要素を参考にしながら制作</span>
            </div>

            {/* Image preview */}
            {imageChild && currentRole !== 'illustration' && (
                <div className="w-full h-32 bg-surface overflow-hidden">
                    <img src={imageChild.fileUrl} alt="既存の絵" className="w-full h-full object-contain" />
                </div>
            )}

            {/* Audio player */}
            {audioChild && currentRole !== 'voice' && (
                <div className="flex items-center gap-3 px-3 py-2 bg-cream-2">
                    <audio ref={audioRef} src={audioChild.fileUrl} onEnded={() => setPlaying(false)} />
                    <button
                        onClick={toggleAudio}
                        className="w-7 h-7 rounded-full bg-gradient-to-r from-cyan-400 to-purple-500 flex items-center justify-center text-ink text-xs hover:scale-110 transition-transform"
                    >
                        {playing ? '⏸' : '▶'}
                    </button>
                    <span className="text-xs text-ink-2">{audioChild.authorName}の声</span>
                </div>
            )}

            {/* Text preview (scrollable) */}
            {textChild && currentRole !== 'script' && (
                <div className="px-3 py-2 max-h-32 overflow-y-auto">
                    <p className="text-xs text-ink-2 whitespace-pre-wrap leading-relaxed">
                        {textChild.textContent || textChild.description || '（テキスト投稿あり）'}
                    </p>
                    <span className="text-[10px] text-ink-3 mt-1 block">{textChild.authorName}の物語</span>
                </div>
            )}
        </div>
    );
};

// ─── Main Modal ─────────────────────────────────────────
export const SubmissionModal: FC<SubmissionModalProps> = ({
    postId, role, existingChildren = [], onClose, onSuccess,
    initialText, initialTitle, initialDescription, initialImageBlob, initialAudioBlob, originRef,
}) => {
    // PIPE-01 モード: originRef があれば「genesis / 派生」選択ステップから開始する
    const pipeMode = !!originRef;
    const [internalRole, setInternalRole] = useState<'script' | 'illustration' | 'voice'>(role ?? 'voice');
    // 作品化の投稿先（'genesis' or 派生先postId）。PIPE-01では pipe_target で確定する。
    const [targetPostId, setTargetPostId] = useState<string>(postId);
    // GEN-01: 呼び出し元で役割が指定済み（ヒーロー3役ボタン・街区CTA）なら選択ステップをスキップし、
    // 未指定のときだけ「どんな役割で始める？」を出す（導線ステップ数を3役で同一に保つ）
    const [step, setStep] = useState<'pipe_target' | 'select_role' | 'meta' | 'record' | 'input' | 'review' | 'uploading' | 'complete'>(
        pipeMode
            ? 'pipe_target'
            : postId === 'genesis'
                ? (role ? 'meta' : 'select_role')
                : ((role ?? 'voice') === 'voice' ? 'record' : 'input')
    );
    const [fileBlob, setFileBlob] = useState<Blob | null>(initialImageBlob ?? initialAudioBlob ?? null);
    const [textInput, setTextInput] = useState(initialText ?? '');
    const [titleInput, setTitleInput] = useState(initialTitle ?? '');
    const [descInput, setDescInput] = useState(initialDescription ?? '');
    const [roleLimits, setRoleLimits] = useState({ text: 1, image: 1, audio: 1 });
    const [duration, setDuration] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [visibility, setVisibility] = useState<'public' | 'supporters_only'>('public');
    // PIPE-01 派生ピッカー用: 募集中（枠が残る）起点作品の一覧
    const [openPosts, setOpenPosts] = useState<Post[] | null>(null);
    const [loadingPosts, setLoadingPosts] = useState(false);
    const { playSound, triggerCelebration } = useGamenics();

    // プリフィル済みコンテンツがある場合の次ステップ（script は編集用input、画像/音声は確認へ）
    const captureOrReview = (): 'input' | 'record' | 'review' => {
        if (internalRole === 'script') return 'input';
        if (fileBlob) return 'review';
        return internalRole === 'voice' ? 'record' : 'input';
    };

    // 派生として出す: 当該役割の募集枠が残る起点作品を読み込む
    const loadOpenPosts = async () => {
        setLoadingPosts(true);
        try {
            const key = internalRole === 'script' ? 'text' : internalRole === 'illustration' ? 'image' : 'audio';
            const all = await api.getGenesisPosts();
            setOpenPosts(all.filter(p => p.roleLimits && p.childCounts && (p.childCounts[key] ?? 0) < (p.roleLimits[key] ?? 0)));
        } catch {
            setOpenPosts([]); // フェイルセーフ: 取れなければ空（genesisは常に選べる）
        } finally {
            setLoadingPosts(false);
        }
    };

    const handleRecordingComplete = (blob: Blob, dur: number) => {
        setFileBlob(blob);
        setDuration(dur);
        setStep('review');
    };

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setFileBlob(file);
            setStep('review');
        }
    };

    const handleTextSubmit = () => {
        if (!textInput.trim()) return;
        const blob = new Blob([textInput], { type: 'text/plain' });
        setFileBlob(blob);
        setStep('review');
    };

    const getPeaks = async (blob: Blob): Promise<string> => {
        const arrayBuffer = await blob.arrayBuffer();
        const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
        const channelData = audioBuffer.getChannelData(0);
        const peakCount = 100;
        const sampleStep = Math.floor(channelData.length / peakCount);
        const peaks = [];
        for (let i = 0; i < peakCount; i++) {
            let max = 0;
            for (let j = 0; j < sampleStep; j++) {
                const val = Math.abs(channelData[i * sampleStep + j]);
                if (val > max) max = val;
            }
            peaks.push(Math.round(max * 100));
        }
        return JSON.stringify(peaks);
    };

    const handleSubmit = async () => {
        if (!fileBlob) return;
        setStep('uploading');
        setError(null);

        try {
            let peaksStr = undefined;
            if (internalRole === 'voice') {
                peaksStr = await getPeaks(fileBlob);
            }

            const fileName = internalRole === 'voice' ? 'recording.webm' : internalRole === 'illustration' ? 'image.png' : 'script.txt';
            const contentType = internalRole === 'voice' ? 'audio' : internalRole === 'illustration' ? 'image' : 'text';

            const result = await api.uploadSubmission(targetPostId, fileBlob, contentType, fileName, duration, [], peaksStr, titleInput || undefined, descInput || undefined, roleLimits, visibility, originRef);

            if (result.success) {
                setStep('complete');
                await triggerCelebration(GamenicsEvent.SUBMISSION_COMPLETE);
                setTimeout(() => {
                    onSuccess(result.id);
                    onClose();
                }, 2000);
            } else {
                setError(result.error || 'アップロードに失敗しました');
                setStep('review');
            }
        } catch (e: any) {
            setError('データの処理に失敗しました: ' + e.message);
            setStep('review');
        }
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-ink/70 backdrop-blur-sm"
                    onClick={onClose}
                />

                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="relative bg-surface border-2 border-ink rounded-pop p-8 w-full max-w-lg  max-h-[90vh] overflow-y-auto"
                >
                    <button
                        onClick={() => { playSound(GamenicsEvent.TAP_CANCEL); onClose(); }}
                        aria-label={COPY.close}
                        className="absolute top-4 right-4 w-9 h-9 rounded-full bg-cream-2 border-2 border-ink hover:bg-sun flex items-center justify-center text-ink z-10"
                    >
                        <Icon name="x" size={16} />
                    </button>

                    <h2 className="text-2xl font-display font-bold text-center mb-6">
                        {step === 'pipe_target' && '作品として届ける'}
                        {step === 'select_role' && 'どんな役割で始める？'}
                        {step === 'meta' && '作品の情報'}
                        {step === 'record' && '声を録音する'}
                        {step === 'input' && internalRole === 'illustration' && '絵をアップロードする'}
                        {step === 'input' && internalRole === 'script' && '物語を書く'}
                        {step === 'review' && '作品の確認'}
                        {step === 'uploading' && 'アップロード中...'}
                        {step === 'complete' && '届けました！'}
                    </h2>

                    {/* ──── Step: PIPE-01 target (genesis or 派生先の起点) ──── */}
                    {step === 'pipe_target' && (
                        <div className="flex flex-col gap-4 py-2">
                            {openPosts === null && !loadingPosts ? (
                                <>
                                    <p className="text-sm text-ink-2 text-center">
                                        この作品を、新しい起点（三位一体の始まり）として届けますか？<br />
                                        それとも、いま募集中の起点に重ねますか？
                                    </p>
                                    <PulseButton
                                        isPrimary
                                        onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setTargetPostId('genesis'); setStep('meta'); }}
                                        className="w-full"
                                    >
                                        新しい起点として届ける
                                    </PulseButton>
                                    <PulseButton
                                        variant="outline"
                                        onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); loadOpenPosts(); }}
                                        className="w-full"
                                    >
                                        募集中の起点に重ねる
                                    </PulseButton>
                                </>
                            ) : loadingPosts ? (
                                <div className="flex justify-center py-8" aria-label="読み込み中">
                                    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                                </div>
                            ) : openPosts && openPosts.length === 0 ? (
                                <div className="text-center py-4">
                                    <p className="text-ink-2 text-sm mb-4">
                                        いま、あなたの{internalRole === 'script' ? '物語' : internalRole === 'illustration' ? '絵' : '声'}を待っている起点はまだないようです。
                                    </p>
                                    <PulseButton isPrimary onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setTargetPostId('genesis'); setStep('meta'); }} className="w-full">
                                        新しい起点として届ける
                                    </PulseButton>
                                    <button onClick={() => { playSound(GamenicsEvent.TAP_CANCEL); setOpenPosts(null); }} className="mt-3 text-xs text-ink-3 underline min-h-[44px]">
                                        ちょっと戻る
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <p className="text-xs font-bold text-ink-2">重ねたい起点をえらぶ（{internalRole === 'script' ? '物語' : internalRole === 'illustration' ? '絵' : '声'}の枠が残っている作品）:</p>
                                    <ul className="flex flex-col gap-2 max-h-64 overflow-y-auto list-none p-0 m-0">
                                        {openPosts?.map(p => (
                                            <li key={p.id}>
                                                <button
                                                    onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setTargetPostId(p.id); setStep(captureOrReview()); }}
                                                    className="w-full text-left px-4 py-3 min-h-[44px] rounded-pop-sm border-2 border-ink bg-cream hover:bg-sun transition-colors flex items-center gap-3"
                                                >
                                                    <span className="flex-1">
                                                        <span className="block font-bold text-ink text-sm">{p.title || '無題の起点'}</span>
                                                        <span className="block text-xs text-ink-2">{p.creatorName}</span>
                                                    </span>
                                                    <Icon name="arrow-right" size={16} />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                    <button onClick={() => { playSound(GamenicsEvent.TAP_CANCEL); setOpenPosts(null); }} className="text-xs text-ink-3 underline min-h-[44px] self-start">
                                        ちょっと戻る
                                    </button>
                                </>
                            )}
                        </div>
                    )}

                    {/* ──── Step: Select Role (genesis only) ──── */}
                    {step === 'select_role' && (
                        /* 3役対等のエントリー（RoleEntryButtonsと同一デザイン・AA配色。
                           旧PulseButton outlineは白地に白文字となり script/illustration が不可視だった） */
                        <div className="py-4">
                            <RoleEntryButtons onSelect={(r) => { setInternalRole(r); setStep('meta'); }} />
                        </div>
                    )}

                    {/* ──── Step: Meta (genesis only) ──── */}
                    {step === 'meta' && (
                        <div className="flex flex-col gap-4">
                            <div>
                                <label className="block text-sm text-ink-2 mb-1">タイトル</label>
                                <input
                                    type="text"
                                    value={titleInput}
                                    onChange={(e) => setTitleInput(e.target.value)}
                                    placeholder="作品に名前をつけよう"
                                    className="w-full bg-cream border-2 border-ink rounded-xl px-4 py-3 text-ink placeholder-gray-500 focus:border-primary focus:outline-none transition-colors"
                                    maxLength={100}
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-ink-2 mb-1">説明（任意）</label>
                                <textarea
                                    value={descInput}
                                    onChange={(e) => setDescInput(e.target.value)}
                                    placeholder="どんな作品？ 一言添えよう"
                                    className="w-full bg-cream border-2 border-ink rounded-xl px-4 py-3 text-ink placeholder-gray-500 focus:border-primary focus:outline-none transition-colors resize-none h-24"
                                    maxLength={500}
                                />
                            </div>

                            <div className="bg-cream p-4 rounded-xl border border-ink/15 mt-2">
                                <h4 className="text-xs font-bold text-ink-3 mb-4 tracking-widest uppercase">共創の枠組みを設定する</h4>
                                <div className="space-y-4">
                                    {(['text', 'image', 'audio'] as const).map((r) => (
                                        <div key={r} className="flex flex-col gap-2">
                                            <div className="flex justify-between items-center">
                                                <span className="text-sm text-ink-2">
                                                    {r === 'text' ? '物語' : r === 'image' ? '絵' : '声'} の募集枠
                                                </span>
                                                <span className="text-sm font-mono text-primary">{roleLimits[r]}</span>
                                            </div>
                                            <input
                                                type="range"
                                                min="0"
                                                max="10"
                                                value={roleLimits[r]}
                                                onChange={(e) => setRoleLimits({ ...roleLimits, [r]: parseInt(e.target.value) })}
                                                className="w-full h-1 bg-cream-2 rounded-lg appearance-none cursor-pointer accent-primary"
                                            />
                                        </div>
                                    ))}
                                </div>
                                <p className="text-[10px] text-ink-3 mt-4 leading-relaxed">
                                    ※ 0に設定するとその役割の募集を停止します。
                                </p>
                            </div>
                            <PulseButton
                                isPrimary
                                onClick={() => {
                                    playSound(GamenicsEvent.TAP_CONFIRM);
                                    setStep(captureOrReview());
                                }}
                                className="w-full"
                                disabled={!titleInput.trim()}
                            >
                                {COPY.confirm}
                            </PulseButton>
                        </div>
                    )}

                    {/* ──── Step: Record Voice ──── */}
                    {step === 'record' && internalRole === 'voice' && (
                        <div>
                            {/* Show existing text/image while recording */}
                            <ExistingPreview children={existingChildren} currentRole="voice" />
                            <VoiceRecorder onRecordingComplete={handleRecordingComplete} />
                        </div>
                    )}

                    {/* ──── Step: Input Image ──── */}
                    {step === 'input' && internalRole === 'illustration' && (
                        <div className="flex flex-col gap-4">
                            <ExistingPreview children={existingChildren} currentRole="illustration" />
                            <label className="border-2 border-dashed border-ink/40 rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors">
                                <span className="mb-4 text-ink-3 inline-flex"><Icon name="art" size={40} /></span>
                                <span className="text-ink-2">画像を選択してください</span>
                                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                            </label>
                        </div>
                    )}

                    {/* ──── Step: Input Text/Script ──── */}
                    {step === 'input' && internalRole === 'script' && (
                        <div className="flex flex-col gap-4">
                            <ExistingPreview children={existingChildren} currentRole="script" />
                            <textarea
                                value={textInput}
                                onChange={e => setTextInput(e.target.value)}
                                placeholder="物語を入力してください..."
                                className="w-full h-48 bg-cream border border-ink/15 rounded-xl p-4 text-ink resize-none focus:border-primary focus:outline-none transition-colors"
                            />
                            <PulseButton isPrimary onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); handleTextSubmit(); }} disabled={!textInput.trim()}>
                                {COPY.confirm}
                            </PulseButton>
                        </div>
                    )}

                    {/* ──── Step: Review ──── */}
                    {step === 'review' && fileBlob && (
                        <div className="flex flex-col gap-6">
                            <div className="bg-cream p-4 rounded-lg flex flex-col items-center max-h-64 overflow-hidden">
                                {internalRole === 'voice' && (
                                    <audio controls src={URL.createObjectURL(fileBlob)} className="w-full" />
                                )}
                                {internalRole === 'illustration' && (
                                    <img src={URL.createObjectURL(fileBlob)} alt="Preview" className="max-h-56 object-contain" />
                                )}
                                {internalRole === 'script' && (
                                    <div className="w-full h-full overflow-y-auto text-left text-sm whitespace-pre-wrap p-2 text-ink-2">
                                        {textInput}
                                    </div>
                                )}
                            </div>

                            {error && (
                                <p className="text-red-500 text-sm text-center bg-red-500/10 p-2 rounded">{error}</p>
                            )}

                            <div className="flex items-center justify-center gap-2 mb-1 text-sm">
                                <span className="text-ink-2 font-bold">公開範囲:</span>
                                <select value={visibility} onChange={(e) => setVisibility(e.target.value as 'public' | 'supporters_only')} className="pop-field text-sm">
                                    <option value="public">全体公開</option>
                                    <option value="supporters_only">応援フォロワー限定</option>
                                </select>
                            </div>

                            <div className="flex gap-4">
                                <PulseButton
                                    variant="outline"
                                    onClick={() => { playSound(GamenicsEvent.TAP_CANCEL); setStep(internalRole === 'voice' ? 'record' : 'input'); setFileBlob(null); }}
                                    className="flex-1"
                                >
                                    {COPY.retry}
                                </PulseButton>
                                <PulseButton
                                    isPrimary
                                    onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); handleSubmit(); }}
                                    className="flex-1"
                                >
                                    {COPY.submit}
                                </PulseButton>
                            </div>
                        </div>
                    )}

                    {step === 'uploading' && (
                        <div className="flex justify-center py-12">
                            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                    )}

                    {step === 'complete' && (
                        <div className="text-center py-8">
                            <div className="mb-4 text-role-voice flex justify-center"><Icon name="heart" size={48} /></div>
                            <p className="text-ink-2">素晴らしい表現です！ありがとう！</p>
                        </div>
                    )}
                </motion.div>
            </div>
        </AnimatePresence>
    );
};
